import { useDisclosure } from '@chakra-ui/react';
import { isNil } from 'lodash-es';

import { getVideoContentType } from '@services/query-services/default.query-service';
import { fetchBlobAs } from '@util/helpers';
import {
  useMeetingTimeProgress,
  useMeetingBotStatuses,
  useMeetingTimeStatus,
  useMeetingPauseResume,
  useMeetingRecordingStatus,
  useRemoveFora
} from '@util/hooks/meeting.hooks';

const useMeetingViewVideo = (meeting) => {
  return async () => {
    const { videoUrl } = meeting;
    const { contentType } = await getVideoContentType(videoUrl);

    if (contentType === 'video/mp4') {
      window.open(videoUrl, '_blank');
    } else {
      const videoBlob = await fetchBlobAs(videoUrl, 'video/mp4');
      const videoBlobUrl = URL.createObjectURL(videoBlob);
      window.open(videoBlobUrl, '_blank', {});
    }
  };
};

export const useMeetingManagementActions = (meeting, bot) => {
  const addForaModalDisclosure = useDisclosure();
  const { isWithin15MinutesBeforeStart, isWithin1HourAfterMeetingEnds } = useMeetingTimeProgress(
    meeting,
    bot
  );
  const {
    botWasScheduled,
    botWasKicked,
    botWasNeverAdmitted,
    recordingWasStarted,
    isCallEnded,
    isLive,
    hasTranscripts,
    insufficientTranscripts,
    isRecordingAcquired,
    wasSummarized,
    isJoining,
    inWaitingRoom
  } = useMeetingBotStatuses(bot);
  const meetingTimeStatus = useMeetingTimeStatus(meeting);
  const { isPausingResuming, pauseResume } = useMeetingPauseResume(meeting, bot);
  const onViewVideoClick = useMeetingViewVideo(meeting);
  const { startMeetingAndRecording, isStartingMeeting, isStartingRecording } =
    useMeetingRecordingStatus(meeting, bot);

  const { removeFora, isRemovingFora } = useRemoveFora(meeting, bot);

  const botHadFailures = !botWasScheduled || botWasNeverAdmitted || botWasKicked;
  const isAfterMeeting = botHadFailures ? meetingTimeStatus === 'done' : isCallEnded;

  const isMeetingUpload = meeting?.tags?.includes('meeting_upload');

  // https://foraday.atlassian.net/browse/FORA-1174
  // just check if (startAt - 15min) < currentTime < endAt
  const isJoinable = isWithin15MinutesBeforeStart || meetingTimeStatus === 'live';

  const showAddForaButton =
    !recordingWasStarted && !botWasScheduled && meetingTimeStatus !== 'done';
  const showAddForaBackButton =
    botWasScheduled &&
    !isJoining &&
    !inWaitingRoom &&
    isCallEnded &&
    insufficientTranscripts &&
    isWithin1HourAfterMeetingEnds &&
    !isMeetingUpload;
  const showStartEarlyButton =
    botWasScheduled &&
    !isAfterMeeting &&
    !isJoining &&
    !inWaitingRoom &&
    !isLive &&
    !isCallEnded &&
    !isMeetingUpload;
  const showPauseResumeButton = recordingWasStarted && !botHadFailures && !isAfterMeeting; // note isJoinable might be redundant?

  const showUploadVideoButton =
    !isMeetingUpload &&
    ((!botWasScheduled && meetingTimeStatus === 'done') ||
      (isCallEnded && insufficientTranscripts));

  return {
    // Joining
    showJoinButton: !isNil(meeting?.url) && isJoinable && !isMeetingUpload,

    // Transcripts
    transcriptAvailable: (isLive || isCallEnded) && hasTranscripts,

    isShareable: wasSummarized,

    // Video
    showViewVideoButton: (!!bot?.video_url || isRecordingAcquired) && isCallEnded,
    onViewVideoClick,
    showUploadVideoButton,

    // Start Recording
    startMeetingAndRecording,
    isStartingRecording,
    isStartingMeeting,
    showStartEarlyButton,

    // Adding/Removing Fora
    showAddForaButton,
    showAddForaBackButton,
    addForaModalDisclosure,
    removeFora,
    isRemovingFora,

    // Pause/Resuming
    isPausingResuming,
    showPauseResumeButton,
    pauseResume
  };
};
